/**
 * Project Query Hooks
 *
 * TanStack Query hooks for project data, all routed through the API server.
 */

import { useQuery } from '@tanstack/react-query';

import { useFetchWithAuth } from './use-api';

/** Project shape from the API */
interface ApiProject {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  updated_at: string;
  [key: string]: unknown;
}

/** Request shape from project requests endpoint */
interface ApiProjectRequest {
  id: string;
  title: string;
  description: string | null;
  status: string;
  priority: string;
  type: string;
  project_id: string;
  request_number: number;
  updated_at: string;
  created_at: string;
  [key: string]: unknown;
}

export function useProjectBySlug(slug: string) {
  const fetchWithAuth = useFetchWithAuth();

  return useQuery({
    queryKey: ['project', slug],
    queryFn: async () => {
      const res = await fetchWithAuth(
        `/projects/by-slug/${encodeURIComponent(slug)}`
      );
      if (!res.ok) {
        if (res.status === 404) throw new Error('Project not found');
        throw new Error('Failed to fetch project');
      }
      const json = await res.json();
      return json.data as ApiProject;
    },
  });
}

export function useProjectRequests(projectId: string | undefined) {
  const fetchWithAuth = useFetchWithAuth();

  return useQuery({
    queryKey: ['project-requests', projectId],
    queryFn: async () => {
      if (!projectId) return [];
      const res = await fetchWithAuth(
        `/projects/${projectId}/requests?sortBy=updated_at&sortOrder=desc&limit=100`
      );
      if (!res.ok) throw new Error('Failed to fetch project requests');
      const json = await res.json();
      return json.data as ApiProjectRequest[];
    },
    enabled: !!projectId,
  });
}

export function useProjectsDropdown() {
  const fetchWithAuth = useFetchWithAuth();

  return useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const res = await fetchWithAuth('/projects?limit=100');
      if (!res.ok) throw new Error('Failed to fetch projects');
      const json = await res.json();
      return json.data as Array<{
        id: string;
        name: string;
        slug: string;
        [key: string]: unknown;
      }>;
    },
  });
}
